import { useEffect, useState } from "react";
import { useAuth } from "@clerk/clerk-react";
import { syncUser } from "../../services/api/app/auth";
import { checkFirstTime } from "../../services/api/app/user";
import logger from "../../utils/logger";

/**
 * Shows the welcome ClaimCredits popup after login when the backend reports
 * the user has not been welcomed yet (first-time / unclaimed welcome flow).
 */
export function useWelcomeCreditsPopup({ enabled = true } = {}) {
  const { isLoaded, isSignedIn, getToken } = useAuth();
  const [show, setShow] = useState(false);
  const [checked, setChecked] = useState(false);

  useEffect(() => {
    if (!enabled || !isLoaded || !isSignedIn || checked) return;

    let cancelled = false;

    (async () => {
      try {
        const token = await getToken();
        if (!token || cancelled) return;

        try {
          await syncUser(token);
        } catch (error) {
          logger.warn("Welcome popup: user sync failed", error);
        }

        const result = await checkFirstTime(token);
        if (!cancelled && result?.is_first_time) {
          setShow(true);
        }
      } catch (error) {
        logger.warn("Welcome popup: first-time check failed", error);
      } finally {
        if (!cancelled) {
          setChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [enabled, isLoaded, isSignedIn, checked, getToken]);

  const close = () => setShow(false);

  // Hold a landing-page tool popup until this one-time claim is finished.
  const blocksToolEntry = enabled && isSignedIn && (!isLoaded || !checked || show);

  return { show, close, blocksToolEntry };
}
